import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Download, UserPlus, Search, Plus, Upload, Building2, Mail } from 'lucide-react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { usePortfolioCompanies, useSubmissions, useUsersByPortfolioCompany, useSendQuarterlyEmail } from '@lingaai/shared-hooks';
import type { User } from '@lingaai/types';
import { SubmissionDetailDrawer } from '../../../components/SubmissionDetailDrawer';
import { InviteUserDialog } from '../../../components/InviteUserDialog';
import { AddPortfolioCompanyDrawer } from '../../../components/AddPortfolioCompanyDialog';
import {} from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { downloadCSV, downloadExcel, formatMetricValue } from '@lingaai/shared-utils';

interface PortfolioCompaniesTabProps {
  fundId: string;
}

export function PortfolioCompaniesTab({ fundId }: PortfolioCompaniesTabProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [sendingEmailFor, setSendingEmailFor] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: currentUser } = useCurrentUser();

  // Check for search query and tab from URL params (from global search)
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    const urlTab = searchParams.get('tab');
    const urlCompanyId = searchParams.get('companyId');

    if (urlSearch) {
      setSearchQuery(urlSearch);
    }

    // Auto-select the company if companyId is provided
    if (urlCompanyId) {
      setSelectedCompanyId(urlCompanyId);
    }

    // If tab=users, switch to Users tab (index 1)
    if (urlTab === 'users') {
      setActiveTab(1);
    }

    // Clear the params from URL after applying
    if (urlSearch || urlTab || urlCompanyId) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: companies, isLoading: companiesLoading } = usePortfolioCompanies(fundId);
  const { data: submissions, isLoading: submissionsLoading } = useSubmissions(selectedCompanyId || undefined);
  const { data: users, isLoading: usersLoading } = useUsersByPortfolioCompany(selectedCompanyId || '');
  const sendQuarterlyEmail = useSendQuarterlyEmail();

  const userColumns: GridColDef<User>[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
    },
  ];

  const handleSendQuarterlyEmail = async (companyId: string, companyName: string) => {
    if (!currentUser?.tenant_id) {
      setSnackbar({
        open: true,
        message: 'Unable to send email: User session not found',
        severity: 'error',
      });
      return;
    }

    setSendingEmailFor(companyId);

    try {
      const result = await sendQuarterlyEmail.mutateAsync({
        portfolioCompanyId: companyId,
        tenantId: currentUser.tenant_id,
        isTrial: false, // Normal mode - send to portfolio company users
      });

      setSnackbar({
        open: true,
        message: `Quarterly email sent to ${result.recipients.length} recipient(s) at ${companyName} for ${result.period}`,
        severity: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';

      // User-friendly error messages
      let friendlyMessage = errorMessage;
      if (errorMessage.includes('already sent')) {
        friendlyMessage = 'A quarterly email has already been sent for this period.';
      } else if (errorMessage.includes('No portfolio company users')) {
        friendlyMessage = 'No users found for this company. Please invite users first.';
      } else if (errorMessage.includes('not found')) {
        friendlyMessage = 'Portfolio company not found.';
      }

      setSnackbar({
        open: true,
        message: friendlyMessage,
        severity: 'error',
      });
    } finally {
      setSendingEmailFor(null);
    }
  };

  const handleExport = (format: 'csv' | 'excel') => {
    if (!submissions || submissions.length === 0) {
      alert('No submissions available to export');
      setExportAnchor(null);
      return;
    }

    const selectedCompany = companies?.find((c) => c.id === selectedCompanyId);

    // Flatten all metrics from all submissions for this company
    const exportData: any[] = [];
    submissions.forEach((submission: any) => {
      if (submission.metrics && submission.metrics.length > 0) {
        submission.metrics.forEach((metric: any) => {
          exportData.push({
            Period: submission.period,
            Metric: metric.metric_definitions?.name || 'Unknown',
            Value: formatMetricValue(metric.value),
            Confidence: metric.confidence ? `${(metric.confidence * 100).toFixed(0)}%` : '-',
            Source: metric.source_type || '-',
            Reviewed: metric.reviewed ? 'Yes' : 'No',
            Status: submission.status,
            Submitted: submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : '-',
          });
        });
      }
    });

    if (exportData.length === 0) {
      alert('No metrics found to export');
      setExportAnchor(null);
      return;
    }

    const filename = selectedCompany?.name.replace(/\s+/g, '-') || 'company-submissions';

    if (format === 'csv') {
      downloadCSV(exportData, filename);
    } else {
      downloadExcel(exportData, filename);
    }

    setExportAnchor(null);
  };

  if (companiesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const downloadTemplate = () => {
    const templateData = [
      {
        'Company Name': 'Acme Corp',
        Website: 'acme.com',
        Sector: 'SaaS',
        'Investment Date': '2024-01-15',
        'CEO Email': 'john@acme.com',
        'CEO Name': 'John Smith',
        'CFO Email': 'jane@acme.com',
        'CFO Name': 'Jane Doe',
      },
      {
        'Company Name': 'TechFlow Inc',
        Website: 'techflow.io',
        Sector: 'FinTech',
        'Investment Date': '2023-06-20',
        'CEO Email': 'ceo@techflow.io',
        'CEO Name': 'Bob Johnson',
        'CFO Email': 'cfo@techflow.io',
        'CFO Name': 'Alice Williams',
      },
    ];
    downloadCSV(templateData, 'portfolio-companies-template');
  };

  const handleImportClick = () => {
    navigate('/dashboard', { state: { message: 'I want to import portfolio companies' } });
  };

  if (!companies || companies.length === 0) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '500px',
            p: 4,
          }}
        >
          <Building2 size={48} strokeWidth={1.5} style={{ opacity: 0.5 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Add Your Portfolio Companies
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, textAlign: 'center' }}>
            Import your portfolio companies and their contacts to start collecting quarterly data. Contacts will receive email invitations to submit metrics.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" startIcon={<Upload size={16} strokeWidth={2} />} onClick={handleImportClick}>
              Import from CSV
            </Button>
            <Button variant="outlined" startIcon={<Plus size={16} strokeWidth={2} />} onClick={() => setAddCompanyDialogOpen(true)}>
              Add Manually
            </Button>
          </Box>

          <Button size="small" variant="text" startIcon={<Download size={14} strokeWidth={2} />} onClick={downloadTemplate} sx={{ mb: 3 }}>
            Download CSV Template
          </Button>

          <Box sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1, maxWidth: 600 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>CSV Format:</strong> Include company name, website, and contact emails. Our AI will automatically map your columns. Supports multiple contacts per company (CEO, CFO, etc.)
            </Typography>
          </Box>
        </Box>

        <AddPortfolioCompanyDrawer open={addCompanyDialogOpen} onClose={() => setAddCompanyDialogOpen(false)} fundId={fundId} tenantId={currentUser?.tenant_id || ''} />
      </>
    );
  }

  const filteredCompanies = companies.filter((company) => company.name.toLowerCase().includes(searchQuery.toLowerCase()) || company.sector?.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 200px)' }}>
      <Box sx={{ width: 280, flexShrink: 0 }}>
        <Paper sx={{ height: '100%', overflow: 'auto' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Companies ({companies.length})</Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<Plus size={16} strokeWidth={2} />}
              sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
              onClick={() => setAddCompanyDialogOpen(true)}
            >
              Add Company
            </Button>
          </Box>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List>
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <ListItem
                  key={company.id}
                  disablePadding
                  secondaryAction={
                    <Tooltip title="Send Quarterly Email">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent selecting company
                          handleSendQuarterlyEmail(company.id, company.name);
                        }}
                        disabled={sendingEmailFor === company.id}
                        sx={{ mr: 0.5 }}
                      >
                        {sendingEmailFor === company.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Mail size={18} />
                        )}
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton selected={selectedCompanyId === company.id} onClick={() => setSelectedCompanyId(company.id)}>
                    <ListItemText primary={company.name} secondary={company.sector || 'No sector'} />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No companies found" secondary={searchQuery ? 'Try a different search term' : 'No companies available'} sx={{ textAlign: 'center', color: 'text.secondary' }} />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>

      <Box sx={{ flex: 1 }}>
        {selectedCompanyId ? (
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 2,
              }}
            >
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab label="Submissions" />
                <Tab label="Users" />
              </Tabs>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {activeTab === 0 && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download size={16} strokeWidth={2} />}
                      onClick={(e) => setExportAnchor(e.currentTarget)}
                      sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
                    >
                      Export
                    </Button>
                    <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
                      <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                      <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                    </Menu>
                  </>
                )}
                {activeTab === 1 && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UserPlus size={16} strokeWidth={2} />}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
                    onClick={() => setInviteDialogOpen(true)}
                  >
                    Invite User
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {activeTab === 0 && (
                <>
                  {submissionsLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Period</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Submitted</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submissions && submissions.length > 0 ? (
                            submissions.map((submission) => {
                              const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
                                draft: 'default',
                                submitted: 'primary',
                                processing: 'warning',
                                completed: 'success',
                                needs_review: 'error',
                              };

                              return (
                                <TableRow key={submission.id} hover onClick={() => setSelectedSubmissionId(submission.id)} sx={{ cursor: 'pointer' }}>
                                  <TableCell>
                                    <Typography variant="body2">{submission.period}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={submission.status} size="small" color={statusColors[submission.status] || 'default'} />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{submission.source_type}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {submission.submitted_at
                                        ? new Date(submission.submitted_at).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                          })
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No submissions found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}

              {activeTab === 1 && (
                <>
                  {usersLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <DataGrid
                      rows={users || []}
                      columns={userColumns}
                      pageSizeOptions={[25, 50, 100]}
                      slots={{
                        toolbar: GridToolbar,
                      }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      disableRowSelectionOnClick
                      sx={{ border: 0 }}
                    />
                  )}
                </>
              )}
            </Box>
          </Paper>
        ) : (
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">Select a company to view details</Typography>
          </Paper>
        )}
      </Box>

      <SubmissionDetailDrawer
        submissionId={selectedSubmissionId}
        open={!!selectedSubmissionId}
        onClose={() => setSelectedSubmissionId(null)}
        onNavigate={setSelectedSubmissionId}
        submissionIds={submissions?.map((s: any) => s.id).filter((id: string) => !!id) || []}
        fundId={fundId}
      />

      {selectedCompanyId && currentUser && (
        <InviteUserDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          portfolioCompanyId={selectedCompanyId}
          portfolioCompanyName={companies?.find((c) => c.id === selectedCompanyId)?.name || 'Portfolio Company'}
          tenantId={currentUser.tenant_id!}
        />
      )}

      <AddPortfolioCompanyDrawer open={addCompanyDialogOpen} onClose={() => setAddCompanyDialogOpen(false)} fundId={fundId} tenantId={currentUser?.tenant_id || ''} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
